import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  FormHelperText,
  InputAdornment
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Event as EventIcon, LocationOn as LocationIcon, Group as GroupIcon } from '@mui/icons-material';
import { useGroups } from '../../contexts/GroupContext';
import { useParties } from '../../contexts/PartyContext';
import { isBefore, startOfDay } from 'date-fns';

export default function PartyForm({ open, onClose, party = null, mode = 'create' }) {
  const { groups } = useGroups();
  const { createParty, updateParty, checkDateConflicts } = useParties();
  const [formData, setFormData] = useState({
    name: '',
    date: null,
    location: '',
    notes: '',
    groupId: '',
    maxAttendees: '',
    isPrivate: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [dateConflicts, setDateConflicts] = useState([]);
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  // Initialize form with party data if editing
  useEffect(() => {
    if (party && mode === 'edit') {
      setFormData({
        name: party.name || '',
        date: party.date ? new Date(party.date) : null,
        location: party.location || '',
        notes: party.notes || '',
        groupId: party.groupId || '',
        maxAttendees: party.maxAttendees || '',
        isPrivate: party.isPrivate || false
      });
    } else {
      setFormData({
        name: '',
        date: null,
        location: '',
        notes: '',
        groupId: '',
        maxAttendees: '',
        isPrivate: false
      });
    }
    setErrors({});
    setSubmitError('');
    setDateConflicts([]);
  }, [party, mode, open]);

  const checkDateConflictsForGroup = useCallback(async () => {
    if (!formData.date || !formData.groupId) return;

    setCheckingConflicts(true);
    try {
      const conflicts = await checkDateConflicts(formData.groupId, formData.date);
      setDateConflicts(conflicts);
    } catch (error) {
      console.error('Error checking date conflicts:', error);
      setDateConflicts([]);
    } finally {
      setCheckingConflicts(false);
    }
  }, [formData.date, formData.groupId, checkDateConflicts]);

  // Check for date conflicts when date or group changes
  useEffect(() => {
    if (formData.date && formData.groupId) {
      checkDateConflictsForGroup();
    } else {
      setDateConflicts([]);
    }
  }, [formData.date, formData.groupId, checkDateConflictsForGroup]);

  // Check for conflicts when form opens in edit mode with existing data
  useEffect(() => {
    if (open && mode === 'edit' && party && party.date && party.groupId) {
      // Small delay to ensure form data is fully initialized
      const timer = setTimeout(() => {
        if (formData.date && formData.groupId) {
          checkDateConflictsForGroup();
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [open, mode, party, formData.date, formData.groupId, checkDateConflictsForGroup]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Party name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Party name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Party name must be less than 100 characters';
    }

    if (!formData.date) {
      newErrors.date = 'Party date is required';
    } else if (isBefore(startOfDay(formData.date), startOfDay(new Date()))) {
      newErrors.date = 'Party date cannot be in the past';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    } else if (formData.location.trim().length > 200) {
      newErrors.location = 'Location must be less than 200 characters';
    }

    if (!formData.groupId) {
      newErrors.groupId = 'Please select a group';
    }

    if (formData.notes.trim().length > 1000) {
      newErrors.notes = 'Notes must be less than 1000 characters';
    }

    if (formData.maxAttendees && (isNaN(formData.maxAttendees) || formData.maxAttendees < 1)) {
      newErrors.maxAttendees = 'Max attendees must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError('');

    try {
      const selectedGroup = groups.find(g => g.id === formData.groupId);
      
      const partyData = {
        ...formData,
        groupName: selectedGroup?.name || '',
        maxAttendees: formData.maxAttendees ? parseInt(formData.maxAttendees) : null
      };

      if (mode === 'create') {
        await createParty(partyData);
      } else {
        await updateParty(party.id, partyData);
      }
      
      onClose();
    } catch (error) {
      console.error('PartyForm: Error submitting form:', error);
      setSubmitError(error.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const isFormValid = formData.name.trim() && formData.date && formData.location.trim() && formData.groupId;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        {mode === 'create' ? 'Create New Party' : 'Edit Party'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Party Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name || 'Enter a name for your party (2-100 characters)'}
            margin="normal"
            required
            disabled={loading}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EventIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Party Date"
              value={formData.date}
              onChange={(newDate) => handleInputChange('date', newDate)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  margin="normal"
                  required
                  disabled={loading}
                  error={!!errors.date}
                  helperText={errors.date || 'Select the date for your party'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EventIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              minDate={new Date()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "normal",
                  required: true,
                  disabled: loading,
                  error: !!errors.date,
                  helperText: errors.date || 'Select the date for your party'
                }
              }}
            />
          </LocalizationProvider>

          <TextField
            fullWidth
            label="Location"
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            error={!!errors.location}
            helperText={errors.location || 'Where will the party take place?'}
            margin="normal"
            required
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl fullWidth margin="normal" disabled={loading} error={!!errors.groupId}>
            <InputLabel>Select Group</InputLabel>
            <Select
              value={formData.groupId}
              label="Select Group"
              onChange={(e) => handleInputChange('groupId', e.target.value)}
              required
            >
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon color="action" />
                    <Box>
                      <Typography variant="body2">{group.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {group.memberCount || 0} members
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {errors.groupId && <FormHelperText>{errors.groupId}</FormHelperText>}
          </FormControl>

          {groups.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You need to create or join a group before you can create a party.
            </Alert>
          )}

          <TextField
            fullWidth
            label="Max Attendees (Optional)"
            value={formData.maxAttendees}
            onChange={(e) => handleInputChange('maxAttendees', e.target.value)}
            error={!!errors.maxAttendees}
            helperText={errors.maxAttendees || 'Maximum number of people who can attend (leave blank for unlimited)'}
            margin="normal"
            disabled={loading}
            type="number"
            inputProps={{ min: 1 }}
          />

          <TextField
            fullWidth
            label="Notes (Optional)"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            error={!!errors.notes}
            helperText={errors.notes || 'Additional details about the party (max 1000 characters)'}
            margin="normal"
            multiline
            rows={3}
            disabled={loading}
          />

          {/* Date Conflict Warnings */}
          {dateConflicts.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Date Conflict Warning:</strong> The following group members are unavailable on this date:
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {dateConflicts.map((conflict, index) => (
                  <Chip
                    key={index}
                    label={conflict.userName}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                Consider choosing a different date or contacting these members to check availability.
              </Typography>
            </Alert>
          )}

          {checkingConflicts && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Checking group member availability...
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> After creating the party, you can send invitations to all group members using the invitation system.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !isFormValid || groups.length === 0}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading 
              ? (mode === 'create' ? 'Creating...' : 'Saving...') 
              : (mode === 'create' ? 'Create Party' : 'Save Changes')
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
